import { Box, Paper, Typography, useTheme } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useCelloData } from '../../hooks/useCelloData';
import { useTranslation } from '../../hooks/useTranslation';
import { useCelloService } from '../../services/cello/cello-api-service';
import { useGoogleChatNotificationService } from '../../services/googleChatNotification.service';
import { saveUserIntegrationsStandalone } from '../../services/user-integrations.service';
import { useUserService } from '../../services/user.service';
import { QUERY_KEYS } from '../../utils/queryKeys';
import CountrySelection from './steps/CountrySelection';
import ExpertCompletionStep from './steps/ExpertCompletionStep';
import ExpertSelectionStep from './steps/ExpertSelectionStep';
import PlanSelectionStep from './steps/PlanSelectionStep';

const getSteps = (t: (key: string) => string) => [t('onboarding.steps.country'), '', '', ''];

const OnboardingPage = () => {
    const { t } = useTranslation();
    const steps = getSteps(t);
    const { currentUser, currentCtUser, setIsNewUser, refreshToken } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const queryClient = useQueryClient();
    const [activeStep, setActiveStep] = useState(0);
    const [selectedCountry, setSelectedCountry] = useState('Germany');
    const [selectedPlan, setSelectedPlan] = useState<'diy' | 'expert' | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const theme = useTheme();
    const userService = useUserService();
    const { sendRegistrationNotification } = useGoogleChatNotificationService();
    const celloApiService = useCelloService();
    const { data: ucc } = useCelloData();

    useEffect(() => {
        queryClient.prefetchQuery({
            queryKey: [QUERY_KEYS.INTEGRATIONS],
        });
    }, [queryClient]);

    // Handle URL parameter for expert assignment
    useEffect(() => {
        if (searchParams.has('assigned_to')) {
            // User is coming back from expert booking, set to expert completion step
            setActiveStep(3);
            setSelectedPlan('expert');
        }
    }, [searchParams]);

    if (!currentUser) {
        return <Navigate to="/login" />;
    }

    const completeOnboardingProcess = async () => {
        try {
            // --- Cello Integration: Start ---
            if (ucc && typeof ucc === 'string' && ucc.trim() !== '') {
                try {
                    console.log(`[OnboardingPage] UCC found: ${ucc}. Calling completeSignupProcess.`);
                    if (currentUser && currentCtUser) {
                        await celloApiService.completeSignupProcess(
                            ucc,
                            currentUser.uid,
                            currentUser.email || '',
                            currentUser.displayName || '',
                            currentCtUser.currency || 'EUR'
                        );
                        console.log('[OnboardingPage] completeSignupProcess call successful.');
                    } else {
                        console.warn(
                            '[OnboardingPage] currentUser or currentCtUser not available. Skipping completeSignupProcess.'
                        );
                    }
                } catch (celloError) {
                    console.error('[OnboardingPage] Error during Cello completeSignupProcess call:', celloError);
                }
            } else {
                console.log('[OnboardingPage] No valid UCC found from useCelloData. Skipping completeSignupProcess.');
            }
            // --- Cello Integration: End ---

            // Complete onboarding
            await userService.finihsOnboarding();
            await sendRegistrationNotification();

            // Set integration setup state to 'pending' so navigation is restricted
            // until user adds their first integration
            try {
                await saveUserIntegrationsStandalone([], 'pending');
                // Immediately update the query cache to reflect the pending state
                queryClient.setQueryData(['integrationSetupState'], 'pending');
                // Also invalidate to ensure fresh data on next fetch
                queryClient.invalidateQueries({ queryKey: ['integrationSetupState'] });
            } catch (error) {
                console.error('Error setting integration setup state:', error);
            }

            await refreshToken();
            setIsNewUser(false);
        } catch (error) {
            console.error('Error completing onboarding:', error);
            // Set integration setup state to 'pending' even on error
            try {
                await saveUserIntegrationsStandalone([], 'pending');
                // Immediately update the query cache to reflect the pending state
                queryClient.setQueryData(['integrationSetupState'], 'pending');
                // Also invalidate to ensure fresh data on next fetch
                queryClient.invalidateQueries({ queryKey: ['integrationSetupState'] });
            } catch (setupError) {
                console.error('Error setting integration setup state:', setupError);
            }
            setIsNewUser(false);
        }
    };

    const handleNext = async () => {
        // Expert completion step - go to integrations
        if (activeStep === 3 && selectedPlan === 'expert') {
            if (isLoading) return; // Prevent multiple clicks
            
            setIsLoading(true);
            try {
                await completeOnboardingProcess();
                navigate('/integrations');
            } catch (error) {
                console.error('Error during expert onboarding:', error);
                setIsLoading(false);
            }
            return;
        }

        // Default progression for other steps
        if (activeStep === 0) {
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.INTEGRATIONS] });
        }

        setActiveStep((prevActiveStep) => prevActiveStep + 1);
    };

    const handleSelectDiy = async () => {
        if (isLoading) return; // Prevent multiple clicks
        
        setIsLoading(true);
        setSelectedPlan('diy');
        
        try {
            // Complete onboarding immediately and go to integrations
            await completeOnboardingProcess();
            navigate('/integrations');
        } catch (error) {
            console.error('Error during DIY onboarding:', error);
            setIsLoading(false);
        }
    };

    const handleSelectExpert = () => {
        setSelectedPlan('expert');
        // Move to expert selection step
        setActiveStep(2);
    };
    
    const handleExpertRedirect = () => {
        // Redirect to external booking page
        // The external page will redirect back to /onboarding?assigned_to=<expert_name>
        window.location.href = 'https://calendly.com/mark-coinfox/15min';
    };


    const renderStepContent = () => {
        switch (activeStep) {
            case 0:
                return (
                    <CountrySelection
                        selectedCountry={selectedCountry}
                        onCountryChange={setSelectedCountry}
                        onNext={handleNext}
                    />
                );
            case 1:
                return (
                    <PlanSelectionStep
                        onSelectDiy={handleSelectDiy}
                        onSelectExpert={handleSelectExpert}
                        onNext={handleNext}
                        isDiyLoading={isLoading}
                    />
                );
            case 2:
                // Show expert selection step if expert was selected
                if (selectedPlan === 'expert') {
                    return <ExpertSelectionStep 
                        onSelectExpert={handleExpertRedirect} 
                        onBack={() => setActiveStep(1)} 
                    />;
                }
                return null;
            case 3:
                // Show expert completion step after returning from booking
                if (selectedPlan === 'expert') {
                    return <ExpertCompletionStep onNext={handleNext} />;
                }
                return null;
            default:
                return null;
        }
    };

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '100vh',
                padding: 3,
            }}
        >
            <Paper
                elevation={theme.palette.mode === 'dark' ? 0 : 3}
                sx={{
                    width: '100%',
                    maxWidth: '1100px',
                    overflow: 'hidden',
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: (theme) => theme.palette.divider,
                    boxShadow: (theme) =>
                        `0px 5px 15px ${theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(0, 0, 0, 0.1)'}`,
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        minHeight: { xs: 'auto', sm: '700px' },
                        width: '100%',
                    }}
                >
                    <Box
                        sx={{
                            width: '100%',
                            padding: 4,
                            display: 'flex',
                            flexDirection: 'column',
                            height: '700px',
                            overflowY: 'auto',
                        }}
                    >
                        <Typography
                            component="h1"
                            variant="h4"
                            gutterBottom
                            sx={{
                                fontWeight: 600,
                                marginBottom: 3,
                            }}
                        >
                            {activeStep === 2 && selectedPlan === 'expert'
                                ? ''
                                : steps[activeStep] || ''}
                        </Typography>
                        {renderStepContent()}
                    </Box>
                </Box>
            </Paper>
        </Box>
    );
};

export default OnboardingPage;
